/**
 * @module Button
 */

import {ClientApplication} from '../../client';
import {
  actionWrapper,
  getEventNameSpace,
  getMergedProps,
  sanitizeProps,
  ActionSet,
} from '../helper';
import {ActionSetProps, ClickAction, Component, ComponentType, Group, MetaAction} from '../types';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
  Message as ErrorMessage,
} from '../Error';
import {Action, ActionType, ClickPayload, Options, Payload} from './types';

export interface ButtonUpdateAction extends MetaAction {
  readonly group: string;
  payload: Payload;
}

export type ButtonAction = ButtonUpdateAction | ClickAction | MetaAction;

export function getSafeButtonProps(props: Payload) {
  return sanitizeProps(props, ['label', 'primary', 'disabled', 'id']);
}

export function clickButton(
  group: string,
  component: Component,
  payload?: ClickPayload,
): ClickAction {
  const {id} = component;
  const action = getEventNameSpace(group, Action.CLICK, component);
  const buttonPayload: ClickPayload = {
    id,
    payload,
  };

  return actionWrapper({type: action, group, payload: buttonPayload});
}

export function update(group: string, component: Component, props: Payload): ButtonUpdateAction {
  const {id} = component;
  const {label} = props;
  const action = getEventNameSpace(group, Action.UPDATE, component);
  const buttonPayload: Payload = {
    id,
    label,
    ...getSafeButtonProps(props),
  };

  return actionWrapper({type: action, group, payload: buttonPayload});
}

export function validationError(
  action: ButtonUpdateAction | ClickAction | MetaAction,
): undefined | ErrorAction {
  if (!action.hasOwnProperty('payload')) {
    return invalidPayload(action, ErrorMessage.MISSING_PAYLOAD);
  }
  let payload;
  switch (action.type) {
    case ActionType.CLICK:
      payload = (action as ButtonUpdateAction).payload;

      return typeof payload.id === 'string'
        ? undefined
        : invalidPayload(action, ErrorMessage.INVALID_PAYLOAD_ID);

    case ActionType.UPDATE:
      payload = (action as ButtonUpdateAction).payload;

      return isValidButtonProps(payload) ? undefined : invalidPayload(action);
    default:
      return invalidActionType(action);
  }
}

export function isValidButtonProps(button: Payload) {
  return typeof button.id === 'string' && typeof button.label === 'string';
}

export class Button extends ActionSet implements ActionSetProps<Options, Payload> {
  label!: string;
  primary = false;
  disabled = false;
  constructor(app: ClientApplication<any>, options: Options) {
    super(app, ComponentType.Button, Group.Button);
    this.set(options, false);
  }

  get options(): Options {
    return {
      disabled: this.disabled,
      label: this.label,
      primary: this.primary,
    };
  }

  get payload(): Payload {
    return {
      id: this.id,
      ...this.options,
    };
  }

  set(options: Partial<Options>, shouldUpdate = true) {
    const mergedOptions = getMergedProps(this.options, options);
    const {label, primary, disabled} = mergedOptions;

    if (!this.validateOptions(mergedOptions)) {
      return this;
    }

    this.label = label;
    this.primary = !!primary;
    this.disabled = !!disabled;

    if (shouldUpdate) {
      this.dispatch(Action.UPDATE);
    }

    return this;
  }

  dispatch(action: Action.UPDATE): ActionSet;

  dispatch(action: Action.CLICK, payload?: any): ActionSet;

  dispatch(action: Action, payload?: any) {
    switch (action) {
      case Action.CLICK:
        this.app.dispatch(clickButton(this.group, this.component, payload));
        break;
      case Action.UPDATE:
        const updateAction = update(this.group, this.component, this.payload);
        this.app.dispatch(updateAction);
        break;
      default:
        throwError(
          ErrorActionType.INVALID_ACTION_TYPE,
          actionWrapper({
            group: this.group,
            payload: this.payload,
            type: action,
          }),
        );
    }

    return this;
  }

  private validateOptions(options: Options) {
    const buttonProps = {id: this.id, ...options};
    if (!isValidButtonProps(buttonProps)) {
      const errorAction = update(this.group, this.component, buttonProps);
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);

      return false;
    }

    return true;
  }
}

export function create(app: ClientApplication<any>, options: Options) {
  return new Button(app, options);
}



// WEBPACK FOOTER //
// ./src/actions/Button/actions.ts